<script lang="ts" setup>
import { onMounted, onUnmounted, ref, watch } from 'vue';
import { useElementBounding } from '@vueuse/core';

import type { Location } from '@libs/maps';
import {
  AppMessageBox,
  AppSvgIcon,
  AppTag,
  AppToggle,
  AppCheckbox,
} from '@libs/ui';
import type {
  FlightAutoCompleteSuggestion,
  PortInfo,
} from '@libs/trip-planner-interfaces';

import { AutoCompleteType, AutoCompleteSuggestions } from '../autoComplete';
import { AirportSelectorMobile } from '../airportSelector';
import { useBrowserStore } from '../../core/stores';

import type {
  AddFlightsModalInitialRoute,
  AddFligthsModalRoute,
} from './addFlightsModal.model';
import { useAddFlighstModalStore } from './store/addFlightsModalStore';

interface AddFlightsViewNewProps {
  routes: AddFlightsModalInitialRoute[];
  startPositon: Location | undefined;
  endPosition: Location | undefined;
  isEditing?: boolean;
}

const props = defineProps<AddFlightsViewNewProps>();

interface AddFlightsViewNewEmits {
  (
    event: 'airport-data-to-be-updated',
    airportSuggestion: FlightAutoCompleteSuggestion | undefined
  ): void;
}

const emit = defineEmits<AddFlightsViewNewEmits>();

const addFlightsModalStore = useAddFlighstModalStore();
const browserStore = useBrowserStore();

watch(
  () => props.routes,
  () => {
    addFlightsModalStore.initializeRoutes(
      props.routes,
      props.startPositon?.position,
      props.endPosition?.position
    );
  },
  { immediate: true }
);

function updateAirport(airportSuggestion: FlightAutoCompleteSuggestion): void {
  addFlightsModalStore.updateAirport(airportSuggestion);
}

function saveAirportDataToBeUpdated(
  airportSuggestion: FlightAutoCompleteSuggestion | undefined
): void {
  emit('airport-data-to-be-updated', airportSuggestion);
}

function openAirportSelection(
  airport: PortInfo,
  index: number,
  isEditingFromAirport: boolean
): void {
  addFlightsModalStore.setEditingAirport(airport, index, isEditingFromAirport);
}

function toggleRoute(route: AddFligthsModalRoute): void {
  addFlightsModalStore.toggleRoute(route);
}

const addFlightsRouteListRef = ref<HTMLElement | null>(null);

const { bottom } = useElementBounding(addFlightsRouteListRef);

function onDirectFlightChange(route: AddFligthsModalRoute): void {
  addFlightsModalStore.toggleDirectFlight(route);
}

function closeAirportSelection(): void {
  if (addFlightsModalStore.isEditing && browserStore.isSmallerThan900px) {
    saveAirportDataToBeUpdated(undefined);
  }

  addFlightsModalStore.clearEditingAirport();
}

onMounted(() => {
  document.body.style.overflow = 'hidden';
});

onUnmounted(() => {
  document.body.style.overflow = '';
});
</script>

<template>
  <template
    v-if="addFlightsModalStore.isEditing && browserStore.isSmallerThan900px"
  >
    <AirportSelectorMobile
      :initial-airport="addFlightsModalStore.editingAirport?.label"
      :is-departing-airport="addFlightsModalStore.isEditingFrom"
      @airport-selected="saveAirportDataToBeUpdated"
    />
  </template>
  <template v-else>
    <div class="add-flights-route-list-wrapper">
      <slot />
      <div ref="addFlightsRouteListRef" class="add-flights-route-list">
        <div
          v-for="(route, index) in addFlightsModalStore.routes"
          :key="index"
          class="add-flights-route"
        >
          <div class="route-header">
            <AppToggle
              :checked="route.isEnabled"
              label-position="right"
              size="m"
              :aria-label="
                (route.isEnabled
                  ? $t('modals.add_flights_new.toggle_on_alt')
                  : $t('modals.add_flights_new.toggle_off_alt')) +
                ` ${route.toAirport.label}`
              "
              @toggle="toggleRoute(route)"
            >
              <span class="label">
                {{
                  route.isEnabled
                    ? $t('modals.add_flights_new.with_flight')
                    : $t('modals.add_flights_new.without_flight')
                }}
              </span>
            </AppToggle>

            <AppCheckbox
              :checked="route.isDirect"
              :is-disabled="!route.isEnabled"
              theme="secondary"
              size="small"
              @change="route.isEnabled && onDirectFlightChange(route)"
            >
              <span>
                {{ $t('modals.add_flights_new.direct_flights') }}
              </span>
            </AppCheckbox>
          </div>
          <AppMessageBox
            v-if="
              route.fromAirport.code &&
              route.toAirport.code &&
              route.fromAirport.code === route.toAirport.code &&
              route.isEnabled
            "
            :text="$t('modals.addFlights.different_airports_validation')"
            message-type="error-light"
            :with-border="true"
            class="message-box"
          >
            <template #left-icon>
              <AppSvgIcon name="infoCircle" color="color-neutral-70" />
            </template>
          </AppMessageBox>

          <div
            class="route-body"
            :class="{
              disabled: !route.isEnabled,
            }"
          >
            <AutoCompleteSuggestions
              class="airport-selection-button"
              :auto-complete-type="AutoCompleteType.Airport"
              :initial-search-text="route.fromAirport.label ?? ''"
              :placeholder="$t('modals.addFlights.choose_departing_airport')"
              :initial-user-position="startPositon?.position"
              :disable="!route.isEnabled"
              :wrapper-bottom-boundary="bottom"
              @on-input-click="
                openAirportSelection(route.fromAirport, index, true)
              "
              @airport-selected="updateAirport($event)"
              @close="closeAirportSelection"
            >
              <template #left-icon>
                <AppSvgIcon name="airplaneTakeoff" />
              </template>
            </AutoCompleteSuggestions>
            <AutoCompleteSuggestions
              class="airport-selection-button"
              :auto-complete-type="AutoCompleteType.Airport"
              :initial-search-text="route.toAirport.label ?? ''"
              :placeholder="$t('modals.addFlights.choose_arriving_airport')"
              :initial-user-position="startPositon?.position"
              :disable="!route.isEnabled"
              :wrapper-bottom-boundary="bottom"
              @on-input-click="
                openAirportSelection(route.toAirport, index, false)
              "
              @airport-selected="updateAirport($event)"
              @close="closeAirportSelection"
            >
              <template #left-icon>
                <AppSvgIcon name="airplaneLanding" />
              </template>
            </AutoCompleteSuggestions>
          </div>
          <AppTag
            v-if="route.nightStayLabel"
            class="stay-info"
            type="grey-new"
            size="s"
          >
            <template #title>
              {{ route.nightStayLabel }}
            </template>
          </AppTag>
        </div>
      </div>
    </div>
  </template>
</template>

<style lang="scss" scoped>
.add-flights-route-list-wrapper {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-12);

  .add-flights-route-list {
    --card-spacing: 51px;

    display: flex;
    flex-direction: column;
    height: 100%;
    gap: var(--card-spacing);

    @include for-less-or-equal-than-899-px {
      margin-bottom: 0;
      --card-spacing: 55px;
    }

    .add-flights-route {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-12);
      position: relative;
      background-color: var(--color-white);

      border: 1px solid var(--color-neutral-30);
      border-radius: var(--border-radius-8);
      box-shadow: var(--shadow-elevation-20);

      padding: var(--spacing-16);

      @include for-less-or-equal-than-899-px {
        padding: var(--spacing-12) var(--spacing-12) var(--spacing-16);
      }

      &:not(:last-child)::after {
        content: '';
        position: absolute;

        bottom: calc(var(--card-spacing) * -1 + 2px);

        left: 0;
        width: var(--spacing-40);
        height: calc(var(--card-spacing) - 8px);

        border-right: 1px dashed var(--color-soft-black);
      }

      .route-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: var(--spacing-8) 0;

        .route-description {
          @include font-body-m-light;
        }

        .label {
          @include font-label-m-light;
          padding-left: var(--spacing-12);
        }
      }

      .message-box {
        justify-content: flex-start;
      }

      .route-body {
        display: flex;
        flex-direction: row;
        gap: var(--spacing-24);
        position: relative;

        .airport-selection-button:first-child::after {
          content: '';
          top: calc(56px / 2);
          right: calc(-1 * calc(var(--spacing-20) + 2px));
          width: var(--spacing-20);
          border-top: 1px dashed var(--color-neutral-60);
          position: absolute;
        }

        &.disabled {
          .airport-selection-button:first-child::after {
            border-top: 1px dashed var(--color-neutral-40);
          }
        }

        @include for-less-or-equal-than-899-px {
          flex-direction: column;
          gap: var(--spacing-8);

          .airport-selection-button:first-child::after {
            display: none;
          }
        }
      }

      .stay-info {
        position: absolute;
        bottom: calc(calc(var(--card-spacing) - 12px) * -1);
        left: var(--spacing-48);

        @include for-less-or-equal-than-899-px {
          bottom: calc(calc(var(--card-spacing) - 14px) * -1);
        }
      }
    }
  }
}
</style>
